import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateAdditionalDetails } from '../store/slices/additionalDetailsSlice';
import { sendProfileUpdateNotification } from '../emailjs/emailService';
import { HiOutlineX, HiOutlineExclamationCircle } from 'react-icons/hi';
import './DeclarationModal.scss';

const DeclarationModal = ({ isOpen, onClose, declarationDetails, userEmail }) => {
  const dispatch = useDispatch();
  const { updating, error } = useSelector(state => state.additionalDetails);
  const { user } = useSelector(state => state.auth);
  const [formData, setFormData] = useState({
    id: '',
    declarationHighestQualification: '',
    declarationConvictionStatus: 'NO',
    declarationConvictionDetails: '',
    declarationDisabilityStatus: 'NO',
    declarationDisabilityDetails: '',
    userEmail: userEmail || '',
  });
  
  // Initialize form data when declarationDetails change
  useEffect(() => {
    if (declarationDetails) {
      setFormData({
        id: declarationDetails.id || '',
        declarationHighestQualification: declarationDetails.declarationHighestQualification || '',
        declarationConvictionStatus: declarationDetails.declarationConvictionStatus || 'NO',
        declarationConvictionDetails: declarationDetails.declarationConvictionDetails || '',
        declarationDisabilityStatus: declarationDetails.declarationDisabilityStatus || 'NO',
        declarationDisabilityDetails: declarationDetails.declarationDisabilityDetails || '',
        userEmail: userEmail || declarationDetails.userEmail || '',
      });
    } else {
      // Ensure userEmail is set even for new records
      setFormData(prev => ({
        ...prev,
        userEmail: userEmail || '',
      }));
    }
  }, [declarationDetails, userEmail]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Reset details field if status is set to NO
      ...(value === 'NO' && name === 'declarationConvictionStatus' && { declarationConvictionDetails: '' }),
      ...(value === 'NO' && name === 'declarationDisabilityStatus' && { declarationDisabilityDetails: '' })
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate fields if YES is selected
    if (formData.declarationConvictionStatus === 'YES' && !formData.declarationConvictionDetails.trim()) {
      alert('Please provide details about your conviction.');
      return;
    }
    
    if (formData.declarationDisabilityStatus === 'YES' && !formData.declarationDisabilityDetails.trim()) {
      alert('Please provide details about your disability.');
      return;
    }
    
    try {
      // Check what fields have changed
      const originalData = {
        declarationHighestQualification: declarationDetails?.declarationHighestQualification || '',
        declarationConvictionStatus: declarationDetails?.declarationConvictionStatus || 'NO',
        declarationConvictionDetails: declarationDetails?.declarationConvictionDetails || '',
        declarationDisabilityStatus: declarationDetails?.declarationDisabilityStatus || 'NO',
        declarationDisabilityDetails: declarationDetails?.declarationDisabilityDetails || ''
      };
      
      const updatedFields = {};
      Object.keys(originalData).forEach(key => {
        if (formData[key] !== originalData[key]) {
          updatedFields[key] = formData[key];
        }
      });
      
      await dispatch(updateAdditionalDetails(formData)).unwrap();
      
      // Send email notification if there were changes
      if (Object.keys(updatedFields).length > 0 && user) {
        try {
          await sendProfileUpdateNotification(
            user,
            'declaration',
            updatedFields
          );
          console.log('Declaration details update notification sent successfully');
        } catch (emailError) {
          console.error('Failed to send declaration details update notification:', emailError);
          // Don't fail the whole operation if email fails
        }
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to update declaration details:', error);
    }
  };
  
  // Handle close when clicking outside the modal
  const handleOutsideClick = (e) => {
    if (e.target.className === 'modal-backdrop') {
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-backdrop" onClick={handleOutsideClick}>
      <div className="modal-container">
        <div className="modal-header">
          <h2>Personal Declaration</h2>
          <button className="close-button" onClick={onClose} aria-label="Close">
            <HiOutlineX />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="declaration-form">
          <div className="form-section">
            <h3>Educational Qualifications</h3>
            <div className="form-group">
              <label htmlFor="declarationHighestQualification">Please detail your highest qualifications</label>
              <textarea
                id="declarationHighestQualification"
                name="declarationHighestQualification"
                value={formData.declarationHighestQualification}
                onChange={handleChange}
                placeholder="Enter details of your degrees, certifications, or other qualifications"
                rows="4"
              />
              <p className="field-hint">Include institution name, qualification title, grade achieved and years attended</p>
            </div>
          </div>
          
          <div className="form-section">
            <h3>Criminal Convictions</h3>
            <div className="radio-group">
              <div className="radio-label">Do you have any unspent criminal convictions?</div>
              <div className="radio-options">
                <label className="radio-container">
                  <input
                    type="radio"
                    name="declarationConvictionStatus"
                    value="NO"
                    checked={formData.declarationConvictionStatus === 'NO'}
                    onChange={handleRadioChange}
                  />
                  <span className="radio-text">No</span>
                </label>
                <label className="radio-container">
                  <input
                    type="radio"
                    name="declarationConvictionStatus"
                    value="YES"
                    checked={formData.declarationConvictionStatus === 'YES'}
                    onChange={handleRadioChange}
                  />
                  <span className="radio-text">Yes</span>
                </label>
              </div>
            </div>
            
            {formData.declarationConvictionStatus === 'YES' && (
              <div className="form-group">
                <label htmlFor="declarationConvictionDetails">
                  Please provide details about your conviction
                </label>
                <textarea
                  id="declarationConvictionDetails"
                  name="declarationConvictionDetails"
                  value={formData.declarationConvictionDetails}
                  onChange={handleChange}
                  placeholder="Include the nature of the offense, date, and any relevant information"
                  rows="3"
                  required
                />
              </div>
            )}
          </div>
          
          <div className="form-section">
            <h3>Disability Declaration</h3>
            <div className="radio-group">
              <div className="radio-label">Do you have a disability or health condition that we should be aware of?</div>
              <div className="radio-options">
                <label className="radio-container">
                  <input
                    type="radio"
                    name="declarationDisabilityStatus"
                    value="NO"
                    checked={formData.declarationDisabilityStatus === 'NO'}
                    onChange={handleRadioChange}
                  />
                  <span className="radio-text">No</span>
                </label>
                <label className="radio-container">
                  <input
                    type="radio"
                    name="declarationDisabilityStatus"
                    value="YES"
                    checked={formData.declarationDisabilityStatus === 'YES'}
                    onChange={handleRadioChange}
                  />
                  <span className="radio-text">Yes</span>
                </label>
              </div>
              <p className="field-hint">This information will be used only for providing appropriate support in the workplace</p>
            </div>
            
            {formData.declarationDisabilityStatus === 'YES' && (
              <div className="form-group">
                <label htmlFor="declarationDisabilityDetails">
                  Please provide details about your disability or health condition
                </label>
                <textarea
                  id="declarationDisabilityDetails"
                  name="declarationDisabilityDetails"
                  value={formData.declarationDisabilityDetails}
                  onChange={handleChange}
                  placeholder="Include any accommodations or support you may require"
                  rows="3"
                  required
                />
              </div>
            )}
          </div>
          
          {error && (
            <div className="error-message">
              <HiOutlineExclamationCircle className="error-icon" />
              <span>{error}</span>
            </div>
          )}
          
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="save-button" disabled={updating}>
              {updating ? 'Saving...' : 'Save Declaration'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DeclarationModal; 